import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, api } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  ArrowLeft, Send, User, Loader2, MessageSquare, Search,
  Phone, Video, MoreVertical, Paperclip, Smile, Check, CheckCheck
} from 'lucide-react';

// Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_creator-brand-2/artifacts/feyyw8lo_145437-200.png";

const Messages = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);
  
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showMobileList, setShowMobileList] = useState(true);

  const fetchConversations = useCallback(async () => {
    try {
      const response = await api.get('/messages/conversations');
      setConversations(response.data.conversations || []);
      if (response.data.conversations?.length > 0 && !activeConversation) {
        setActiveConversation(response.data.conversations[0]);
        setShowMobileList(false);
      }
    } catch (error) {
      console.error('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  }, [activeConversation]);

  const fetchMessages = useCallback(async (conversationId) => {
    try {
      const response = await api.get(`/messages/conversations/${conversationId}`);
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Failed to load messages');
    }
  }, []);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  useEffect(() => {
    if (activeConversation) {
      fetchMessages(activeConversation.conversation_id);
      
      // Poll for new messages every 3 seconds
      pollIntervalRef.current = setInterval(() => {
        fetchMessages(activeConversation.conversation_id);
      }, 3000);
      
      return () => {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
        }
      };
    }
  }, [activeConversation, fetchMessages]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !activeConversation || sending) return;

    setSending(true);
    try {
      await api.post(`/messages/conversations/${activeConversation.conversation_id}`, {
        content: newMessage.trim()
      });
      setNewMessage('');
      await fetchMessages(activeConversation.conversation_id);
      await fetchConversations();
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const getOtherParticipant = (conversation) => {
    if (!conversation?.participant_details) return { name: 'Unknown', picture: '' };
    const other = conversation.participant_details.find(p => p.user_id !== user?.user_id);
    return other || { name: 'Unknown', picture: '' };
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  const filteredConversations = conversations.filter(conv => {
    const other = getOtherParticipant(conv);
    return other.name?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  if (loading) {
    return (
      <div className="min-h-screen dashboard-bg flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#3AAFB9]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen dashboard-bg relative overflow-hidden">
      {/* Creative Background */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="creator-orb creator-orb-pink" style={{ top: '-10%', right: '5%', opacity: 0.3 }} />
        <div className="creator-orb creator-orb-teal" style={{ bottom: '10%', left: '-5%', opacity: 0.25 }} />
        <div className="network-lines opacity-20" />
      </div>

      <div className="relative z-10 h-screen flex flex-col">
        {/* Header */}
        <header className="glass-nav px-4 py-3 flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div className="flex items-center gap-2">
            <img src={LOGO_URL} alt="CmdOut" className="w-8 h-8" />
            <h1 className="text-xl font-bold">Messages</h1>
          </div>
        </header>

        {/* Main Chat Area */}
        <div className="flex-1 flex overflow-hidden">
          {/* Conversations List */}
          <div className={`w-full md:w-80 lg:w-96 border-r bg-white/80 backdrop-blur-sm flex flex-col ${
            !showMobileList && activeConversation ? 'hidden md:flex' : 'flex'
          }`}>
            {/* Search */}
            <div className="p-4 border-b">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search conversations..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-gray-50 border-0"
                />
              </div>
            </div>

            {/* Conversations */}
            <ScrollArea className="flex-1">
              {filteredConversations.length === 0 ? (
                <div className="p-8 text-center">
                  <MessageSquare className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No conversations yet</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Start a conversation from a creator's profile
                  </p>
                </div>
              ) : (
                <div className="divide-y">
                  {filteredConversations.map(conv => {
                    const other = getOtherParticipant(conv);
                    const isActive = activeConversation?.conversation_id === conv.conversation_id;
                    
                    return (
                      <div
                        key={conv.conversation_id}
                        onClick={() => {
                          setActiveConversation(conv);
                          setShowMobileList(false);
                        }}
                        className={`p-4 cursor-pointer transition-all hover:bg-gray-50 ${
                          isActive ? 'bg-[#3AAFB9]/10 border-l-4 border-[#3AAFB9]' : ''
                        }`}
                        data-testid={`conversation-${conv.conversation_id}`}
                      >
                        <div className="flex items-center gap-3">
                          {other.picture ? (
                            <img 
                              src={other.picture} 
                              alt={other.name}
                              className="w-12 h-12 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#3AAFB9] to-[#E8795A] flex items-center justify-center text-white font-bold text-lg">
                              {other.name?.charAt(0) || '?'}
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <h3 className="font-semibold truncate">{other.name}</h3>
                              <span className="text-xs text-muted-foreground">
                                {formatTime(conv.last_message_at)}
                              </span>
                            </div>
                            <p className="text-sm text-muted-foreground truncate">
                              {conv.last_message || 'No messages yet'}
                            </p>
                          </div>
                          {conv.unread_count > 0 && (
                            <div className="w-5 h-5 rounded-full bg-[#E8795A] text-white text-xs flex items-center justify-center">
                              {conv.unread_count}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </div>

          {/* Chat Area */}
          <div className={`flex-1 flex flex-col bg-gradient-to-b from-gray-50 to-white ${
            showMobileList && !activeConversation ? 'hidden md:flex' : 'flex'
          }`}>
            {activeConversation ? (
              <>
                {/* Chat Header */}
                <div className="px-4 py-3 border-b bg-white/90 backdrop-blur-sm flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="md:hidden"
                      onClick={() => setShowMobileList(true)}
                    >
                      <ArrowLeft className="w-4 h-4" />
                    </Button>
                    {(() => {
                      const other = getOtherParticipant(activeConversation);
                      return (
                        <>
                          {other.picture ? (
                            <img 
                              src={other.picture} 
                              alt={other.name}
                              className="w-10 h-10 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#3AAFB9] to-[#E8795A] flex items-center justify-center text-white font-bold">
                              {other.name?.charAt(0) || '?'}
                            </div>
                          )}
                          <div>
                            <h3 className="font-semibold">{other.name}</h3>
                            <p className="text-xs text-green-500">Online</p>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="icon" className="text-muted-foreground">
                      <Phone className="w-5 h-5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="text-muted-foreground">
                      <Video className="w-5 h-5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="text-muted-foreground">
                      <MoreVertical className="w-5 h-5" />
                    </Button>
                  </div>
                </div>

                {/* Messages */}
                <ScrollArea className="flex-1 p-4">
                  <div className="space-y-4 max-w-3xl mx-auto">
                    {messages.map((msg, idx) => {
                      const isOwn = msg.sender_id === user?.user_id;
                      const showAvatar = idx === 0 || messages[idx - 1]?.sender_id !== msg.sender_id;
                      
                      return (
                        <div
                          key={msg.message_id || idx}
                          className={`flex items-end gap-2 ${isOwn ? 'justify-end' : 'justify-start'}`}
                        >
                          {!isOwn && showAvatar && (
                            msg.sender_picture ? (
                              <img 
                                src={msg.sender_picture} 
                                alt=""
                                className="w-8 h-8 rounded-full object-cover"
                              />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center text-sm font-medium">
                                {msg.sender_name?.charAt(0) || '?'}
                              </div>
                            )
                          )}
                          {!isOwn && !showAvatar && <div className="w-8" />}
                          
                          <div className={`max-w-[70%] ${isOwn ? 'order-1' : ''}`}>
                            <div
                              className={`px-4 py-2 rounded-2xl ${
                                isOwn
                                  ? 'bg-gradient-to-r from-[#3AAFB9] to-[#2F9AA3] text-white rounded-br-md'
                                  : 'bg-white shadow-sm border rounded-bl-md'
                              }`}
                            >
                              <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>
                            </div>
                            <div className={`flex items-center gap-1 mt-1 ${isOwn ? 'justify-end' : ''}`}>
                              <span className="text-xs text-muted-foreground">
                                {formatTime(msg.created_at)}
                              </span>
                              {isOwn && (
                                <CheckCheck className="w-3 h-3 text-[#3AAFB9]" />
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>

                {/* Message Input */}
                <div className="p-4 border-t bg-white/90 backdrop-blur-sm">
                  <form onSubmit={handleSendMessage} className="flex items-center gap-2 max-w-3xl mx-auto">
                    <Button type="button" variant="ghost" size="icon" className="text-muted-foreground">
                      <Paperclip className="w-5 h-5" />
                    </Button>
                    <div className="flex-1 relative">
                      <Input
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Type a message..."
                        className="pr-10 rounded-full bg-gray-50 border-0 h-11"
                        disabled={sending}
                        data-testid="message-input"
                      />
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="icon" 
                        className="absolute right-1 top-1/2 -translate-y-1/2 text-muted-foreground"
                      >
                        <Smile className="w-5 h-5" />
                      </Button>
                    </div>
                    <Button 
                      type="submit" 
                      size="icon"
                      className="rounded-full w-11 h-11 bg-gradient-to-r from-[#3AAFB9] to-[#2F9AA3] hover:shadow-lg hover:shadow-[#3AAFB9]/30"
                      disabled={!newMessage.trim() || sending}
                      data-testid="send-message-btn"
                    >
                      {sending ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Send className="w-5 h-5" />
                      )}
                    </Button>
                  </form>
                </div>
              </>
            ) : (
              /* No Conversation Selected */
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#3AAFB9]/20 to-[#E8795A]/20 flex items-center justify-center mx-auto mb-4">
                    <MessageSquare className="w-10 h-10 text-[#3AAFB9]" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Your Messages</h3>
                  <p className="text-muted-foreground max-w-xs">
                    Select a conversation to start chatting with creators or brands
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Messages;
